// Form workflow
export type FormStep = 'business' | 'location' | 'contact' | 'website' | 'design' | 'review';

export const FORM_STEPS: FormStep[] = ['business', 'location', 'website', 'contact', 'design', 'review'];

// Business entity types
export const LEGAL_ENTITIES = [
  'Ltd',
  'LLC',
  'GmbH',
  'Inc',
  'Corp',
  'PLC',
  'Pty Ltd',
  'S.A.',
  'B.V.',
  'AG',
  'S.L.',
  'S.r.l.',
  'SARL',
  'Oy',
  'AS',
  'AB',
  'Sp. z o.o.',
  's.r.o.',
  'Kft.',
  'd.o.o.',
  'OÜ',
  'SIA',
  'UAB',
  'Lda.',
  'KK',
  'GK',
  'ООО',
  'ТОО',
  'FZ-LLC',
  'PJSC',
  'S.A.S.',
  'A.Ş.',
] as const;

export type LegalEntity = (typeof LEGAL_ENTITIES)[number];

// Industry categories
export const INDUSTRIES = [
  'Business/Corporate',
  'Restaurant/Food',
  'Technology',
  'Healthcare',
  'Real Estate',
  'Fitness/Gym',
  'Education',
  'Travel/Tourism',
  // Legal/Finance is excluded — Google Ads restricts financial services
  // advertising and requires advertiser certification (policy 6023676).
  'Retail/E-commerce',
  'Beauty/Spa',
  'Automotive',
  'Construction',
] as const;

export type Industry = (typeof INDUSTRIES)[number];

// Currency options
export const CURRENCIES = [
  'USD',
  'EUR',
  'GBP',
  'AED',
  'AUD',
  'NZD',
  'SGD',
  'HKD',
  'TWD',
  'JPY',
  'RUB',
  'KZT',
  'BYN',
  'NOK',
  'DKK',
  'SEK',
  'TRY',
  'INR',
  'ZAR',
  'PLN',
  'CZK',
  'CAD',
  'BRL',
  'MXN',
  'ARS',
  'CLP',
  'COP',
] as const;

export type Currency = (typeof CURRENCIES)[number];

// Target regions
export const TARGET_REGIONS = ['EU', 'US', 'UK', 'UAE', 'APAC', 'CIS', 'LATAM', 'Nordic', 'Global'] as const;

export type TargetRegion = (typeof TARGET_REGIONS)[number];

// Design options
export const FONT_FAMILIES = ['Inter', 'Roboto', 'Open Sans', 'Poppins'] as const;

export type FontFamily = (typeof FONT_FAMILIES)[number];

export const BORDER_RADIUSES = ['none', 'sm', 'md', 'lg'] as const;

export type BorderRadius = (typeof BORDER_RADIUSES)[number];

export const STYLE_PRESETS = ['corporate', 'minimal-light', 'gradient', 'nature', 'luxury', 'modern'] as const;

export type StylePreset = (typeof STYLE_PRESETS)[number];

// Language options
export const LANGUAGES = [
  { code: 'en-US', name: 'English (US)' },
  { code: 'en-GB', name: 'English (UK)' },
  { code: 'de-DE', name: 'German' },
  { code: 'fr-FR', name: 'French' },
  { code: 'es-ES', name: 'Spanish' },
  { code: 'it-IT', name: 'Italian' },
  { code: 'nl-NL', name: 'Dutch' },
  { code: 'ar-AE', name: 'Arabic (UAE)' },
  { code: 'en-AU', name: 'English (Australia)' },
  { code: 'en-NZ', name: 'English (New Zealand)' },
  { code: 'en-SG', name: 'English (Singapore)' },
  { code: 'zh-SG', name: 'Chinese (Singapore)' },
  { code: 'en-HK', name: 'English (Hong Kong)' },
  { code: 'zh-HK', name: 'Chinese (Hong Kong)' },
  { code: 'zh-TW', name: 'Chinese (Taiwan)' },
  { code: 'ja-JP', name: 'Japanese' },
  { code: 'ru-RU', name: 'Russian' },
  { code: 'kk-KZ', name: 'Kazakh' },
  { code: 'be-BY', name: 'Belarusian' },
  { code: 'fi-FI', name: 'Finnish' },
  { code: 'nb-NO', name: 'Norwegian' },
  { code: 'da-DK', name: 'Danish' },
  { code: 'sv-SE', name: 'Swedish' },
  { code: 'pt-PT', name: 'Portuguese (Portugal)' },
  { code: 'pt-BR', name: 'Portuguese (Brazil)' },
  { code: 'tr-TR', name: 'Turkish' },
  { code: 'hi-IN', name: 'Hindi' },
  { code: 'en-IN', name: 'English (India)' },
  { code: 'en-ZA', name: 'English (South Africa)' },
  { code: 'af-ZA', name: 'Afrikaans' },
  { code: 'pl-PL', name: 'Polish' },
  { code: 'cs-CZ', name: 'Czech' },
  { code: 'de-AT', name: 'German (Austria)' },
  { code: 'nl-BE', name: 'Dutch (Belgium)' },
  { code: 'fr-BE', name: 'French (Belgium)' },
  { code: 'en-IE', name: 'English (Ireland)' },
  { code: 'el-CY', name: 'Greek (Cyprus)' },
  { code: 'fr-LU', name: 'French (Luxembourg)' },
  { code: 'de-LU', name: 'German (Luxembourg)' },
  { code: 'mt-MT', name: 'Maltese' },
  { code: 'en-MT', name: 'English (Malta)' },
  { code: 'sl-SI', name: 'Slovenian' },
  { code: 'hr-HR', name: 'Croatian' },
  { code: 'en-CA', name: 'English (Canada)' },
  { code: 'fr-CA', name: 'French (Canada)' },
  { code: 'lt-LT', name: 'Lithuanian' },
  { code: 'lv-LV', name: 'Latvian' },
  { code: 'et-EE', name: 'Estonian' },
  { code: 'es-MX', name: 'Spanish (Mexico)' },
  { code: 'es-AR', name: 'Spanish (Argentina)' },
  { code: 'es-CL', name: 'Spanish (Chile)' },
  { code: 'es-CO', name: 'Spanish (Colombia)' },
  { code: 'en-JP', name: 'English (Japan)' },
  { code: 'ru-KZ', name: 'Russian (Kazakhstan)' },
  { code: 'ru-BY', name: 'Russian (Belarus)' },
  { code: 'en-FI', name: 'English (Finland)' },
  { code: 'en-NO', name: 'English (Norway)' },
  { code: 'en-DK', name: 'English (Denmark)' },
  { code: 'en-SE', name: 'English (Sweden)' },
  { code: 'en-TR', name: 'English (Turkey)' },
  { code: 'ga-IE', name: 'Irish' },
  { code: 'tr-CY', name: 'Turkish (Cyprus)' },
  { code: 'lb-LU', name: 'Luxembourgish' },
] as const;

export type LanguageCode = (typeof LANGUAGES)[number]['code'];

// Compliance thresholds
export const COMPLIANCE_THRESHOLDS = {
  PASS: 80,
  WARN: 50,
} as const;

// Generation status
export const GENERATION_STATUS = {
  PENDING: 'pending',
  GENERATING: 'generating',
  COMPLETE: 'complete',
  FAILED: 'failed',
  CANCELLED: 'cancelled',
} as const;

export type GenerationStatus = (typeof GENERATION_STATUS)[keyof typeof GENERATION_STATUS];
